import React, { useState } from 'react';
import '../styles/Avatar.css';

/**
 * Avatar Component
 * Displays user profile image or monogram fallback
 *
 * Props:
 * - name: User's full name (required for monogram)
 * - imageUrl: URL to profile image (optional)
 * - size: 'small' (32px), 'medium' (48px), 'large' (80px), 'xlarge' (120px)
 * - clickable: Whether the avatar should have hover effect
 * - onClick: Click handler function
 */
const Avatar = ({ name, imageUrl, size = 'medium', clickable = false, onClick }) => {
  const [imageError, setImageError] = useState(false);

  // Generate monogram from name (first 2 letters)
  const getMonogram = (name) => {
    if (!name) return '?';

    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      // First letter of first name + first letter of last name
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    } else {
      // First 2 letters of single name
      return name.substring(0, 2).toUpperCase();
    }
  };

  // Generate color based on name hash (consistent colors for same names)
  const getColorFromName = (name) => {
    if (!name) return '#8B4513';

    const colors = [
      '#FF6B6B', // Red
      '#4ECDC4', // Teal
      '#45B7D1', // Blue
      '#FFA07A', // Light Salmon
      '#98D8C8', // Mint
      '#F7DC6F', // Yellow
      '#BB8FCE', // Purple
      '#85C1E2', // Sky Blue
      '#F8B739', // Orange
      '#52B788'  // Green
    ];

    // Simple hash function
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  const shouldShowImage = imageUrl && !imageError;
  const backgroundColor = getColorFromName(name);
  const monogram = getMonogram(name);

  const handleImageError = () => {
    setImageError(true);
  };

  const handleClick = () => {
    if (clickable && onClick) {
      onClick();
    }
  };

  return (
    <div
      className={`avatar avatar-${size} ${clickable ? 'avatar-clickable' : ''}`}
      onClick={handleClick}
      style={!shouldShowImage ? { backgroundColor } : {}}
      title={name}
    >
      {shouldShowImage ? (
        <img
          src={imageUrl}
          alt={name}
          className="avatar-image"
          onError={handleImageError}
        />
      ) : (
        <span className="avatar-monogram">{monogram}</span>
      )}
    </div>
  );
};

export default Avatar;
